export interface RecurrenceState {
  recurrence: '' | 'daily' | 'weekly' | 'monthly'
  interval: string
  /** Specific weekdays for a weekly send — 0=Sunday..6=Saturday. Only used when recurrence is
   *  'weekly'; mutually exclusive with `interval` in the UI (see RecurrenceFields below), since
   *  "every 2 weeks on Mon/Wed/Fri" is not a cadence addInterval's weekly branch supports. */
  weekdays: number[]
  endMode: 'never' | 'on' | 'after'
  endAt: string
  endCount: string
}

export const EMPTY_RECURRENCE: RecurrenceState = {
  recurrence: '',
  interval: '',
  weekdays: [],
  endMode: 'never',
  endAt: '',
  endCount: '',
}

export function recurrenceToState(
  recurrence: 'daily' | 'weekly' | 'monthly' | null,
  interval: number | null,
  endAt: string | null,
  endCount: number | null,
  weekdays?: number[] | null,
): RecurrenceState {
  if (!recurrence) return EMPTY_RECURRENCE
  return {
    recurrence,
    interval: interval && interval > 1 ? String(interval) : '',
    weekdays: weekdays ?? [],
    endMode: endAt ? 'on' : endCount ? 'after' : 'never',
    endAt: endAt ? endAt.slice(0, 10) : '',
    endCount: endCount ? String(endCount) : '',
  }
}

/** The five recurrence fields a create/edit form sends the server, derived from the UI state. */
export function recurrencePayload(state: RecurrenceState): {
  recurrence: 'daily' | 'weekly' | 'monthly' | null
  recurrenceInterval: number | null
  recurrenceEndAt: string | null
  recurrenceEndCount: number | null
  recurrenceWeekdays: number[] | null
} {
  if (!state.recurrence) {
    return {
      recurrence: null,
      recurrenceInterval: null,
      recurrenceEndAt: null,
      recurrenceEndCount: null,
      recurrenceWeekdays: null,
    }
  }
  const hasWeekdays = state.recurrence === 'weekly' && state.weekdays.length > 0
  return {
    recurrence: state.recurrence,
    // A day-of-week set replaces the interval rather than combining with it — see the note
    // on RecurrenceState.weekdays.
    recurrenceInterval: hasWeekdays ? null : state.interval.trim() ? Number(state.interval) : null,
    recurrenceEndAt: state.endMode === 'on' && state.endAt ? new Date(state.endAt).toISOString() : null,
    recurrenceEndCount: state.endMode === 'after' && state.endCount.trim() ? Number(state.endCount) : null,
    recurrenceWeekdays: hasWeekdays ? state.weekdays : null,
  }
}

const UNIT_LABEL: Record<'daily' | 'weekly' | 'monthly', string> = {
  daily: 'day(s)',
  weekly: 'week(s)',
  monthly: 'month(s)',
}

const WEEKDAY_LABEL = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

/** A short "repeats …" phrase for a run list — "every 2 weeks, 5 sends total", "Mon/Wed/Fri until Mar 1, 2027". */
export function describeRecurrence(
  recurrence: 'daily' | 'weekly' | 'monthly' | null,
  interval: number | null,
  endAt: string | null,
  endCount: number | null,
  weekdays?: number[] | null,
): string {
  if (!recurrence) return ''
  const unit = recurrence === 'daily' ? 'day' : recurrence === 'weekly' ? 'week' : 'month'
  const base =
    weekdays && weekdays.length > 0
      ? weekdays.map((d) => WEEKDAY_LABEL[d]).join('/')
      : interval && interval > 1
        ? `every ${interval} ${unit}s`
        : recurrence
  const end = endAt
    ? `, until ${new Date(endAt).toLocaleDateString()}`
    : endCount
      ? `, ${endCount} send${endCount === 1 ? '' : 's'} total`
      : ''
  return base + end
}

/**
 * "Every N [unit]" or specific weekdays, plus an end condition — the depth Periskope's
 * recurrence picker has that Wami's plain daily/weekly/monthly select didn't. Rendered only
 * once a repeat cadence is chosen; a one-off send has nothing here to fill in.
 */
export function RecurrenceFields({
  state,
  onChange,
  idPrefix,
}: {
  state: RecurrenceState
  onChange: (next: RecurrenceState) => void
  idPrefix: string
}) {
  if (!state.recurrence) return null

  const toggleWeekday = (day: number) => {
    const next = state.weekdays.includes(day)
      ? state.weekdays.filter((d) => d !== day)
      : [...state.weekdays, day].sort((a, b) => a - b)
    onChange({ ...state, weekdays: next })
  }

  return (
    <div className="stack gap-2">
      {state.recurrence === 'weekly' && (
        <div className="row gap-2 wrap" style={{ alignItems: 'center' }}>
          <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>
            On specific days (optional)
          </span>
          <div className="row gap-1 wrap">
            {WEEKDAY_LABEL.map((label, day) => (
              <button
                key={day}
                type="button"
                className="cad-pill"
                data-tone={state.weekdays.includes(day) ? 'accent' : undefined}
                onClick={() => toggleWeekday(day)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="row gap-2 wrap" style={{ alignItems: 'flex-end' }}>
        {/* Picking specific weekdays replaces "every N weeks" rather than combining with it —
            there is no "every 2 weeks on Mon/Wed/Fri" here, so the control is hidden instead
            of accepting a value the backend would silently ignore. */}
        {state.weekdays.length === 0 && (
          <>
            <div className="cad-field">
              <label htmlFor={`${idPrefix}-interval`}>Every</label>
              <input
                id={`${idPrefix}-interval`}
                className="cad-input"
                type="number"
                min={1}
                style={{ width: '80px' }}
                placeholder="1"
                value={state.interval}
                onChange={(e) => onChange({ ...state, interval: e.target.value })}
              />
            </div>
            <span className="cad-muted" style={{ paddingBottom: '10px' }}>
              {UNIT_LABEL[state.recurrence]}
            </span>
          </>
        )}
        <div className="cad-field">
          <label htmlFor={`${idPrefix}-end-mode`}>Ends</label>
          <select
            id={`${idPrefix}-end-mode`}
            className="cad-input"
            value={state.endMode}
            onChange={(e) => onChange({ ...state, endMode: e.target.value as RecurrenceState['endMode'] })}
          >
            <option value="never">Never</option>
            <option value="on">On date</option>
            <option value="after">After N sends</option>
          </select>
        </div>
        {state.endMode === 'on' && (
          <div className="cad-field">
            <label htmlFor={`${idPrefix}-end-at`}>Date</label>
            <input
              id={`${idPrefix}-end-at`}
              className="cad-input"
              type="date"
              value={state.endAt}
              onChange={(e) => onChange({ ...state, endAt: e.target.value })}
            />
          </div>
        )}
        {state.endMode === 'after' && (
          <div className="cad-field">
            <label htmlFor={`${idPrefix}-end-count`}>Sends</label>
            <input
              id={`${idPrefix}-end-count`}
              className="cad-input"
              type="number"
              min={1}
              style={{ width: '80px' }}
              value={state.endCount}
              onChange={(e) => onChange({ ...state, endCount: e.target.value })}
            />
          </div>
        )}
      </div>
    </div>
  )
}
